import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, SlidersHorizontal, X } from "lucide-react";
import { api, cardProduct, offerPct } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useSeo } from "@/lib/seo";
import ProductCard from "@/components/ProductCard";
import { PRICE_GROUPS } from "@/data/products";

const SORTS = [
  { key: "newest", label: "Newest" },
  { key: "discount", label: "Biggest Discount" },
  { key: "price_asc", label: "Price: Low to High" },
  { key: "price_desc", label: "Price: High to Low" },
  { key: "name_asc", label: "Name A–Z" },
];

export default function ShopPage() {
  const { categoryId } = useParams();
  const [params, setParams] = useSearchParams();
  const { catalog, pincodeInfo } = useStore();
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 0 });
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const storeId = pincodeInfo?.store_id || "";

  const q = params.get("q") || "";
  const brandId = params.get("brand_id") || "";
  const sort = params.get("sort") || "newest";
  const maxPrice = params.get("max_price") || "";
  const minOffer = params.get("min_offer") || "";
  const page = Number(params.get("page") || 1);

  const brandMap = useMemo(
    () => Object.fromEntries(catalog.brands.map((b) => [b.id, b.name])),
    [catalog.brands]
  );

  useEffect(() => {
    setLoading(true);
    const query = {
      page, page_size: 24, sort,
      ...(q && { q }),
      ...(brandId && { brand_id: brandId }),
      ...(categoryId && { category_id: categoryId }),
      ...(maxPrice && { max_price: Number(maxPrice) }),
      ...(storeId && { store_id: storeId }),
    };
    api.get("/ecom/catalog/products", { params: query })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0, page: 1, total_pages: 0 }))
      .finally(() => setLoading(false));
  }, [q, brandId, sort, maxPrice, page, categoryId, storeId]);

  const patch = (obj) => {
    const next = new URLSearchParams(params);
    Object.entries(obj).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    if (!("page" in obj)) next.delete("page");
    setParams(next, { preventScrollReset: false });
  };

  const navigate = useNavigate();
  const goCategory = (id) => {
    const next = new URLSearchParams(params);
    next.delete("page");
    const qs = next.toString();
    setFiltersOpen(false);
    navigate(id ? `/category/${id}${qs ? `?${qs}` : ""}` : `/shop${qs ? `?${qs}` : ""}`);
  };

  const heading = categoryId
    ? catalog.categories.find((c) => c.id === categoryId)?.name || "Category"
    : q ? `Results for “${q}”` : "Shop the Sanctuary";

  const location = useLocation();
  const headingPretty = heading.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  const visibleItems = minOffer ? data.items.filter((p) => offerPct(p) >= Number(minOffer)) : data.items;
  useSeo({
    title: `${headingPretty} — Buy Beauty Products Online at Best Price | Soonyoung`,
    description: `Shop ${headingPretty} online at Soonyoung — original, branded cosmetics & beauty products at the best prices in India. Affordable makeup, skincare & more with free delivery over ₹999, shipping all over India.`,
    path: `${location.pathname}${location.search}`,
  });

  const Filters = (
    <div className="space-y-8">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Price Edit</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {PRICE_GROUPS.map((g) => (
            <button
              key={g.key}
              data-testid={`filter-price-${g.key}`}
              onClick={() => patch({ max_price: String(g.max) === maxPrice ? "" : String(g.max) })}
              className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                String(g.max) === maxPrice ? "border-gold bg-gold text-forest" : "border-champagne/50 text-sage hover:border-gold hover:text-gold"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Offers</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {[50, 30, 20, 10].map((m) => (
            <button
              key={m}
              data-testid={`filter-offer-${m}`}
              onClick={() => patch({ min_offer: String(m) === minOffer ? "" : String(m) })}
              className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                String(m) === minOffer ? "border-black bg-black text-white" : "border-champagne/50 text-sage hover:border-black hover:text-cream"
              }`}
            >
              {m}% &amp; above
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Categories</p>
        <ul className="mt-3 max-h-56 space-y-1 overflow-y-auto no-scrollbar">
          <li>
            <button data-testid="filter-category-all" onClick={() => goCategory("")} className={`text-sm ${!categoryId ? "font-bold text-gold" : "text-sage hover:text-gold"}`}>
              All categories
            </button>
          </li>
          {catalog.categories.slice(0, 20).map((c) => (
            <li key={c.id}>
              <button
                data-testid={`filter-category-${c.id}`}
                onClick={() => goCategory(c.id)}
                className={`text-sm transition-colors hover:text-gold ${c.id === categoryId ? "font-bold text-gold" : "text-sage"}`}
              >
                {c.name} {c.product_count > 0 && <span className="text-fern">({c.product_count})</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Houses</p>
        <ul className="mt-3 max-h-56 space-y-1 overflow-y-auto no-scrollbar">
          <li>
            <button data-testid="filter-brand-all" onClick={() => patch({ brand_id: "" })} className={`text-sm ${!brandId ? "font-bold text-gold" : "text-sage hover:text-gold"}`}>
              All houses
            </button>
          </li>
          {catalog.brands.slice(0, 20).map((b) => (
            <li key={b.id}>
              <button
                data-testid={`filter-brand-${b.id}`}
                onClick={() => patch({ brand_id: b.id === brandId ? "" : b.id })}
                className={`text-sm transition-colors hover:text-gold ${b.id === brandId ? "font-bold text-gold" : "text-sage"}`}
              >
                {b.name} {b.product_count > 0 && <span className="text-fern">({b.product_count})</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  return (
    <section data-testid="shop-page" className="mx-auto max-w-7xl px-4 pb-20 pt-28 sm:px-6 sm:pt-32">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">The Full Shelf</p>
          <h1 className="mt-2 font-display text-3xl tracking-tight text-cream sm:text-5xl">{heading}</h1>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-fern" data-testid="shop-result-count">
            {minOffer ? `${visibleItems.length} of ${data.total} products at ${minOffer}%+ offer` : `${data.total} products`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="filters-toggle-btn"
            onClick={() => setFiltersOpen(true)}
            className="flex items-center gap-2 rounded-full border border-champagne/50 px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-cream lg:hidden"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
          </button>
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => patch({ sort: e.target.value })}
            className="rounded-full border border-champagne/50 bg-pine px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-cream focus:border-gold focus:outline-none"
          >
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-10 grid gap-10 lg:grid-cols-[240px_1fr]">
        <aside className="hidden lg:block">{Filters}</aside>

        <div>
          {loading ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4" data-testid="shop-loading">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="aspect-[4/5] animate-pulse rounded-2xl bg-moss" />
              ))}
            </div>
          ) : visibleItems.length === 0 ? (
            <div data-testid="shop-empty" className="flex flex-col items-center rounded-3xl border border-dashed border-champagne/60 py-24 text-center">
              <p className="font-display text-2xl text-cream">The shelf is being stocked</p>
              <p className="mt-2 max-w-sm text-sm text-sage">
                {minOffer ? (
                  <>No {minOffer}%+ offers on this page — browse the <Link to={`/offers?min=${minOffer}`} className="font-bold text-gold hover:underline">full {minOffer}%+ offers list</Link> instead.</>
                ) : "Our HQ is adding products to the e-boutique right now. Check back shortly — or browse another ritual."}
              </p>
            </div>
          ) : (
            <motion.div layout className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4" data-testid="shop-grid">
              {visibleItems.map((p) => (
                <ProductCard key={p.id} p={cardProduct(p, brandMap[p.brand_id])} wide />
              ))}
            </motion.div>
          )}

          {data.total_pages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-3">
              <button
                data-testid="page-prev-btn"
                disabled={page <= 1}
                onClick={() => patch({ page: String(page - 1) })}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-champagne/50 text-cream disabled:opacity-40"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="font-mono text-xs text-sage" data-testid="page-indicator">{page} / {data.total_pages}</span>
              <button
                data-testid="page-next-btn"
                disabled={page >= data.total_pages}
                onClick={() => patch({ page: String(page + 1) })}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-champagne/50 text-cream disabled:opacity-40"
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-[80] lg:hidden" data-testid="filters-sheet">
          <div className="absolute inset-0 bg-cream/40 backdrop-blur-sm" onClick={() => setFiltersOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[75vh] overflow-y-auto rounded-t-3xl border-t border-champagne/50 bg-pine p-6">
            <div className="mb-6 flex items-center justify-between">
              <p className="font-display text-xl text-cream">Filters</p>
              <button data-testid="filters-close-btn" onClick={() => setFiltersOpen(false)} className="flex h-9 w-9 items-center justify-center rounded-full border border-champagne/40 text-cream" aria-label="Close filters">
                <X className="h-4 w-4" />
              </button>
            </div>
            {Filters}
          </div>
        </div>
      )}
    </section>
  );
}
